import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import PortraitIcon from '@mui/icons-material/Portrait';
import RadarIcon from '@mui/icons-material/Radar';
import TodayIcon from '@mui/icons-material/Today';
import WorkspacesIcon from '@mui/icons-material/Workspaces';

import { setCurrentCourse, setCurrentModule } from '../../../../store/courseService';
import { selectUsersForNames } from '../../../../store/userSelectors';
import { formatDate, isTimePast } from '../../../../utils/dateFunctions';
import Button from '../../../formElements/Button';
import FullCourseAttendanceDialog from '../../courseInstances/FullCourseAttendanceDialog';

import card from '../../../../commonStyles/card.module.css';

const ScheduleModuleAttendanceView = ({
    course,
    module,
    prevPage = '',
    isSchedulingDisabled = false,
    isAttendanceAccessDisabled = false,
    isSelfEmployedCoach = false
}) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const {
        title,
        name,
        description,
        locationId,
        date,
        startTime,
        endTime,
        userId,
        capacity,
        smartTarget
    } = module;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [disableAttendanceAccess, setDisableAttendanceAccess] = useState(false);
    const [courseLocation, setCourseLocation] = useState('');
    const [adviser, setAdviser] = useState('');
    const moduleName = name ?? title;

    // STORE STATE
    const locations = useSelector((state) => state.entities.directusService.locationIdsAndNames);
    const users = useSelector(selectUsersForNames);

    // USEEFFECTS
    useEffect(() => {
        setDisableAttendanceAccess(!module?.participants?.length || isAttendanceAccessDisabled);
    }, [module?.participants]);

    useEffect(() => {
        if (adviser || users?.length < 1) return;
        const user = users.find((el) => el.id === userId);
        user && addAdviser(user);
    }, [users]);

    useEffect(() => {
        if (courseLocation || !locations || locations?.length < 1) return;
        const location = locations.find((el) => el.id === locationId);
        location && setCourseLocation(location.name);
    }, [locations]);

    // HELPER FNS
    const addAdviser = (adviser) => {
        const { firstName, lastName } = adviser;
        setAdviser(`${firstName} ${lastName}`);
    };

    // EVENT HANDLERS
    const onSchedule = (e) => {
        e.preventDefault();
        if (isSchedulingDisabled) return;
        dispatch(setCurrentModule(module));
        dispatch(setCurrentCourse(course));
        navigate('/course_calendar', { state: { prevPage } });
    };

    // RENDER
    return (
        <div
            className={`${card.card} ${prevPage === 'editCourse' ? '' : prevPage === 'createCourse' ? card.greyCard : card.blueCard}`}
            data-testid="module_attendance_card_data">
            <div className={`${moduleName?.length > 60 ? card.bannerDoubleRow : card.banner}`}>
                <h6>{moduleName || 'Schedule Module'}</h6>
            </div>

            <div className={card.content}>
                <div>
                    <div className={card.subHeader}>Module Description:</div>
                    <div className={card.description}>
                        {description && description.length > 100
                            ? description.substring(0, 97) + '...'
                            : description}
                    </div>
                </div>
                <div className={`${card.rows} ${card.compactRows}`}>
                    <div className={card.row}>
                        <LocationOnIcon />
                        {` ${courseLocation ? courseLocation : ' - '}`}
                    </div>

                    <div className={card.row}>
                        <TodayIcon />
                        {!date && !startTime && !endTime
                            ? ' - '
                            : ` ${date ? formatDate(date) : ''} ${
                                  startTime ? startTime : ''
                              } - ${endTime ? endTime : ''}`}
                    </div>
                    <div className={card.row}>
                        <PortraitIcon />
                        {adviser || ' - '}
                    </div>
                    <div className={`${card.row} ${card.doubleRowWrapper}`}>
                        <RadarIcon />
                        <span className={card.doubleRow}>{smartTarget?.name || ' - '}</span>
                    </div>
                </div>
                {prevPage !== 'createCourse' ? (
                    <div
                        className={`${prevPage === 'editCourse' ? '' : ''} ${isSchedulingDisabled ? card.nonInteractive : ''}`}>
                        <Button
                            id="scheduleModuleCardButton"
                            type="button"
                            disabled={
                                isSchedulingDisabled ||
                                (date && startTime && isTimePast(`${date}T${startTime}`))
                            }
                            content={
                                !date && !startTime && !endTime
                                    ? 'Schedule this module'
                                    : date && 'Reschedule this module'
                            }
                            customClass="linkButton"
                            onClick={onSchedule}
                        />
                    </div>
                ) : (
                    ''
                )}
                {prevPage !== 'createCourse' && prevPage !== 'editCourse' ? (
                    <div className={card.attendanceView} data-testid="module_attendance_view">
                        <span className={card.spaces}>
                            <WorkspacesIcon />
                            {module.participants && capacity
                                ? `${module.participants.length} of ${capacity} spaces filled`
                                : !capacity && `0 spaces filled`}
                        </span>
                        <div
                            className={`${card.attlink} ${card.link} ${disableAttendanceAccess ? card.linkDisabled : ''}`}
                            onClick={() =>
                                !disableAttendanceAccess ? setIsDialogOpen(true) : null
                            }>
                            See attendance list
                        </div>
                    </div>
                ) : (
                    ''
                )}
                {isDialogOpen && (
                    <FullCourseAttendanceDialog
                        module={module}
                        isSelfEmployedCoach={isSelfEmployedCoach}
                        isOpen={isDialogOpen}
                        onClose={() => setIsDialogOpen(false)}
                    />
                )}
            </div>
        </div>
    );
};

ScheduleModuleAttendanceView.propTypes = {
    course: PropTypes.object.isRequired,
    module: PropTypes.object.isRequired,
    prevPage: PropTypes.string,
    isSchedulingDisabled: PropTypes.bool,
    isAttendanceAccessDisabled: PropTypes.bool,
    isSelfEmployedCoach: PropTypes.bool
};

export default ScheduleModuleAttendanceView;
